import { round } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  mapParameter,
  parameterName,
} from "../../../../parts/other-exercise/CustomParametersSets";
import SvgIcon from "../../../../parts/SvgIcon";
import { getFirstValue, shouldHaveE1RM } from "../../../../utils/actuals";
import { getComplexSetOutcomeReps } from "../../../../utils/complex";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import ExerciseVideoLine from "../../../dashboard/program-builder/components/ExerciseVideoLine";

const {
  PARAMETER_OPTIONS: { FREEFORM, ROUNDS, SETS, LOAD, RPE, RPEAMRAP, REPS },
  OUTCOME_STATUS: { MISSED },
} = require("../../../../constants");

const statusClass = (outcome = {}) => {
  if (outcome.status === MISSED) return " missed";
  return "";
};

const ExercisesList = (props) => {
  const {
    exercises,
    units,
    fromMetric,
    withWeight,
    intensityMeasure,
    isRiR,
    hideVelocitySettings,
    onSort,
  } = props;

  const showE1RM = shouldHaveE1RM(exercises[0]);

  const WeightValue = ({ outcome, param, statusClass = "" }) => {
    const value = outcome?.[param] ? (
      withWeight(
        round(fromMetric(outcome[param], units, false), 1),
        units,
        false
      )
    ) : (
      <>&nbsp;</>
    );
    return <span className={`history-value${statusClass}`}>{value}</span>;
  };

  const VelocityValue = ({ outcome, statusClass = "" }) => {
    const velocity = outcome?.velocity;

    let velocityValue = 0;

    if (velocity) {
      const averageSetVelocity = getFirstValue(velocity?.detailed);
      const hasDetailedData = Boolean(averageSetVelocity);

      velocityValue = hasDetailedData
        ? Number(averageSetVelocity)
        : Number(velocity.simple);
    }

    const value = velocityValue ? `${velocityValue}m/s` : <>&nbsp;</>;

    return <span className={`history-value${statusClass}`}>{value}</span>;
  };

  const ExerciseListHeader = () => {
    const { t } = useTranslation("athlete-portal");
    const { _id: exId, parameters, type } = exercises[0];
    const showPercentRM = type === "primary" || type === "secondary";

    return (
      <thead role="table-header">
        <tr>
          <th className="history-title date">
            <div className="head-item">
              <SvgIcon onClickFn={onSort} src={"/img/svg/up_down_arrows.svg"} />
              {t("exercisesList.date")}{" "}
            </div>
          </th>
          {parameters ? (
            Array.from({ length: parameters.length }, (x, i) =>
              [FREEFORM, ROUNDS, SETS].includes(parameters[i]) ? null : (
                <th
                  key={`${exId}-${parameters[i]}-title`}
                  className={`history-title param ${parameterName(
                    parameters[i]
                  )}`}
                >
                  {[RPE, RPEAMRAP].includes(parameters[i])
                    ? intensityMeasure
                    : parameterName(parameters[i])}
                </th>
              )
            )
          ) : (
            <>
              <th className="history-title param Reps">
                {t("exercisesList.reps")}
              </th>
              <th className="history-title param RPE">{intensityMeasure}</th>
              <th className="history-title param">{t("exercisesList.load")}</th>
            </>
          )}
          {parameters && parameters[0] === FREEFORM && (
            <th className="history-title param" />
          )}
          {showPercentRM && (
            <th className="history-title param prm">
              {t("exercisesList.oneRmPercent")}
            </th>
          )}
          {showE1RM && (
            <th className="history-title param">{t("exercisesList.e1rm")}</th>
          )}
          {showPercentRM && !hideVelocitySettings && (
            <th className="history-title param">
              {t("exercisesList.velocity")}
            </th>
          )}
          <th className="history-title param video">
            {t("exercisesList.video")}
          </th>
          <th className="history-title param video">
            {t("exercisesList.comments")}
          </th>
        </tr>
      </thead>
    );
  };

  const WithParametersList = ({ exercise }) => {
    const exId = exercise._id;
    return exercise.parameters.map((parameter) => {
      if ([SETS, ROUNDS].includes(parameter)) return null;
      const param = mapParameter(parameter);
      return (
        <td key={`${exId}-${parameter}-value`}>
          <div className={`values-column ${parameterName(parameter)}`}>
            {exercise.sets.map((set, setIndex) => {
              return (
                set.outcomes &&
                Object.keys(set.outcomes).map((setOrRoundIndex) => {
                  const outcomes = set.outcomes[setOrRoundIndex];

                  if (parameter === LOAD) {
                    return (
                      <WeightValue
                        key={`${exId}-${setIndex}-${setOrRoundIndex}-load`}
                        outcome={outcomes?.outcome}
                        param="load"
                        statusClass={statusClass(outcomes)}
                      />
                    );
                  }

                  const finalValue = outcomes.outcome
                    ? outcomes.outcome[param]
                    : "";

                  if (parameter === REPS) {
                    return (
                      <span
                        key={`${exId}-${setIndex}-${setOrRoundIndex}-reps`}
                        className={`history-value${statusClass(outcomes)}`}
                      >
                        {finalValue ? finalValue : <>&nbsp;</>}
                      </span>
                    );
                  }

                  return (
                    <span
                      key={`${exId}-${setIndex}-${setOrRoundIndex}-${param}`}
                      className={`history-value${statusClass(outcomes)}`}
                    >
                      {finalValue ? (
                        isRiR ? (
                          convertToRiR(finalValue, !isRiR)
                        ) : (
                          finalValue
                        )
                      ) : (
                        <>&nbsp;</>
                      )}
                    </span>
                  );
                })
              );
            })}
          </div>
        </td>
      );
    });
  };

  const NoParametersList = ({ exercise }) => {
    const exId = exercise._id;
    return (
      <>
        <td>
          <div className="values-column Reps">
            {exercise.sets.map((set, setIndex) => {
              return (
                set.outcomes &&
                Object.keys(set.outcomes).map((setOrRoundIndex) => {
                  const { repsTwo, repsThree } = getComplexSetOutcomeReps(
                    exercise,
                    setIndex,
                    setOrRoundIndex
                  );
                  return (
                    <span
                      key={`${exId}-${setIndex}-${setOrRoundIndex}-reps`}
                      className={`history-value${statusClass(
                        set.outcomes[setOrRoundIndex]
                      )}`}
                    >
                      {set.outcomes[setOrRoundIndex].outcome?.reps}
                      {repsTwo !== undefined && repsTwo !== null
                        ? `+${repsTwo}`
                        : ""}
                      {repsThree !== undefined && repsThree !== null
                        ? `+${repsThree}`
                        : ""}
                    </span>
                  );
                })
              );
            })}
          </div>
        </td>
        <td>
          <div className="values-column RPE">
            {exercise.sets.map((set, setIndex) => {
              return (
                set.outcomes &&
                Object.keys(set.outcomes).map((setOrRoundIndex) => {
                  const { rpe } = set.outcomes[setOrRoundIndex].outcome || {};
                  return (
                    <span
                      key={`${exId}-${setIndex}-${setOrRoundIndex}-rpe`}
                      className={`history-value${statusClass(
                        set.outcomes[setOrRoundIndex]
                      )}`}
                    >
                      {rpe && !Number.isNaN(rpe) ? (
                        convertToRiR(rpe, !isRiR)
                      ) : (
                        <>&nbsp;</>
                      )}
                    </span>
                  );
                })
              );
            })}
          </div>
        </td>
        <td>
          <div className="values-column">
            {exercise.sets.map((set, setIndex) => {
              return (
                set.outcomes &&
                Object.keys(set.outcomes).map((setOrRoundIndex) => (
                  <WeightValue
                    key={`${exId}-${setIndex}-${setOrRoundIndex}-load`}
                    outcome={set.outcomes[setOrRoundIndex].outcome}
                    param="load"
                    statusClass={statusClass(set.outcomes[setOrRoundIndex])}
                  />
                ))
              );
            })}
          </div>
        </td>
      </>
    );
  };

  return (
    <section role="table-wrapper">
      <table role="report-table">
        <ExerciseListHeader />
        <tbody role="table-body">
          {exercises.map((exercise) => {
            const {
              _id: exId,
              primary,
              type,
              repMaximums,
              secondaryRepMaximum,
            } = exercise;
            const showPercentRM = type === "primary" || type === "secondary";
            const isComplex = !!exercise.complex;
            const defaultRepMax =
              (type === "secondary" || isComplex) && secondaryRepMaximum
                ? secondaryRepMaximum
                : repMaximums?.[primary] || 0;
            return (
              <tr key={exId} className="exercises-row values">
                <td className="history-value date">
                  {moment(exercise.utcDate, "YYYYMMDD").format("MMM D, YYYY")}
                </td>
                {exercise.parameters ? (
                  <WithParametersList exercise={exercise} />
                ) : (
                  <NoParametersList exercise={exercise} />
                )}
                {showPercentRM && (
                  <td>
                    <div className="values-column prm">
                      {exercise.sets.map((set, setIndex) => {
                        return (
                          set.outcomes &&
                          Object.keys(set.outcomes).map((setOrRoundIndex) => {
                            const { load } =
                              set.outcomes[setOrRoundIndex].outcome || {};
                            const value =
                              load && defaultRepMax ? (
                                `${round((load / defaultRepMax) * 100, 1)}%`
                              ) : (
                                <>&nbsp;</>
                              );
                            return (
                              <span
                                key={`${exId}-${setIndex}-${setOrRoundIndex}-%rm`}
                                className={`history-value${statusClass(
                                  set.outcomes[setOrRoundIndex]
                                )}`}
                              >
                                {value}
                              </span>
                            );
                          })
                        );
                      })}
                    </div>
                  </td>
                )}
                {showE1RM && (
                  <td>
                    <div className="values-column">
                      {exercise.sets.map((set, setIndex) => {
                        return (
                          set.outcomes &&
                          Object.keys(set.outcomes).map((setOrRoundIndex) => {
                            return (
                              <WeightValue
                                key={`${exId}-${setIndex}-${setOrRoundIndex}`}
                                outcome={set.outcomes[setOrRoundIndex].outcome}
                                param="e1rm"
                                statusClass={statusClass(
                                  set.outcomes[setOrRoundIndex]
                                )}
                              />
                            );
                          })
                        );
                      })}
                    </div>
                  </td>
                )}
                {showPercentRM && !hideVelocitySettings && (
                  <td>
                    <div className="values-column">
                      {exercise.sets.map((set, setIndex) => {
                        return (
                          set.outcomes &&
                          Object.keys(set.outcomes).map((setOrRoundIndex) => {
                            return (
                              <VelocityValue
                                key={`${exId}-${setIndex}-${setOrRoundIndex}-velocity`}
                                outcome={set.outcomes[setOrRoundIndex].outcome}
                                statusClass={statusClass(
                                  set.outcomes[setOrRoundIndex]
                                )}
                              />
                            );
                          })
                        );
                      })}
                    </div>
                  </td>
                )}
                <td>
                  <div className="values-column video">
                    {exercise.sets.map((set, setIndex) => {
                      return (
                        set.outcomes &&
                        Object.keys(set.outcomes).map((setOrRoundIndex) => (
                          <span
                            key={`${exId}-${setIndex}-${setOrRoundIndex}-video`}
                            className="history-value"
                          >
                            {set.videos?.[setOrRoundIndex] ? (
                              <ExerciseVideoLine
                                vidItem={set.videos[setOrRoundIndex]}
                                exerciseToEdit={exercise._id}
                                indexIndividualSet={setOrRoundIndex}
                                setIndex={setIndex}
                                src={"/img/new-icons/play-with-gradient.svg"}
                                utcDate={exercise.utcDate}
                                primary={
                                  exercise.primary ?? exercise.exercise?.primary
                                }
                                secondary={
                                  exercise.secondary ??
                                  exercise.exercise?.secondary
                                }
                                type={exercise.type ?? exercise.exercise?.type}
                                movement={
                                  exercise.movement ??
                                  exercise.exercise?.movement
                                }
                                set={set}
                                outcomeEntry={set.outcomes?.[setOrRoundIndex]}
                                setComment={set.comments?.[setOrRoundIndex]}
                              />
                            ) : (
                              "\u00A0"
                            )}
                          </span>
                        ))
                      );
                    })}
                  </div>
                </td>
                <td>
                  <div className="values-column comments">
                    {exercise.sets.map((set, setIndex) => {
                      return set.comments
                        ? set.outcomes &&
                            Object.keys(set.outcomes).map((setOrRoundIndex) => (
                              <span
                                key={`${setIndex}-${setOrRoundIndex}-comment`}
                              >
                                {set.comments[setOrRoundIndex] || "\u00A0"}
                              </span>
                            ))
                        : set.outcomes &&
                            Object.keys(set.outcomes).map((setOrRoundIndex) => (
                              <span
                                key={`${setIndex}-${setOrRoundIndex}-no-comment`}
                              >
                                &nbsp;
                              </span>
                            ));
                    })}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

export default ExercisesList;
